import type { CalEvent } from "../types";
import type { Person } from "../services/people";

/*
 * "People you work with", for the add-attendee suggestions. Outlook ranks
 * these with Microsoft's People API, which needs People.Read; without it the
 * pane ranks the people on your own meetings instead. A meeting counts for
 * less the more people are on it, so a small 1:1 outweighs an all-hands.
 */

/** The other people on a meeting, by email: everyone invited and the organiser, but not you or rooms. */
function othersOn(e: CalEvent, selfEmails: ReadonlySet<string>): Map<string, string> {
  const people = new Map<string, string>();
  for (const a of e.attendees) if (a.type !== "resource") people.set(a.email, a.name);
  if (e.organizerEmail) people.set(e.organizerEmail, e.organizerName);
  for (const email of selfEmails) people.delete(email);
  return people;
}

/** Everyone on your meetings except you and rooms, best first. */
export function frequentPeople(events: readonly CalEvent[], selfEmails: ReadonlySet<string>): Person[] {
  const scores = new Map<string, { name: string; score: number; last: number }>();
  for (const e of events) {
    if (e.isCancelled) continue;
    const people = othersOn(e, selfEmails);
    if (people.size === 0) continue;
    const weight = 1 / people.size;
    const at = e.start.getTime();
    for (const [email, name] of people) {
      const seen = scores.get(email);
      scores.set(email, {
        name: seen?.name || name,
        score: (seen?.score ?? 0) + weight,
        last: Math.max(seen?.last ?? 0, at),
      });
    }
  }
  return [...scores.entries()]
    .sort(([, a], [, b]) => b.score - a.score || b.last - a.last)
    .map(([email, v]) => ({ name: v.name || email, email }));
}

/** Whether `query` starts a word of the person's name, the whole name, or their email address (case-insensitive). */
export function matchesPerson(p: Person, query: string): boolean {
  const q = query.trim().toLowerCase();
  if (!q) return false;
  const name = p.name.toLowerCase();
  return name.startsWith(q) || p.email.startsWith(q) || name.split(/[\s.,()-]+/).some((word) => word.startsWith(q));
}

/** The domain of an email address, lower-cased; "" when there isn't one. */
export function domainOf(email: string): string {
  const at = email.lastIndexOf("@");
  return at < 0 ? "" : email.slice(at + 1).toLowerCase();
}

/** Outside your organisation: the address isn't on one of your own addresses' domains. Unknown domains count as inside. */
export function isExternal(email: string, ownDomains: ReadonlySet<string>): boolean {
  return ownDomains.size > 0 && !ownDomains.has(domainOf(email));
}
